import type {
  ApiErr,
  Category,
  CategoryInput,
  CategoryPatch,
  Recipe,
  RecipeInput,
  RecipePatch,
  RecipeSummary,
} from '@shared/schemas';
import { PHOTO_WIDTHS, type PhotoVariant } from './image';

async function unwrap<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!res.ok) {
    let message = res.statusText || `HTTP ${res.status}`;
    try {
      const body = JSON.parse(text) as ApiErr;
      if (body?.error?.message) message = body.error.message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  if (!text) return undefined as T;
  const body = JSON.parse(text) as { data: T };
  return body.data;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export const api = {
  // public
  listRecipes: (category?: string) =>
    fetch(`/api/recipes${category ? `?category=${encodeURIComponent(category)}` : ''}`).then(
      unwrap<RecipeSummary[]>,
    ),
  getRecipeBySlug: (slug: string) =>
    fetch(`/api/recipes/${encodeURIComponent(slug)}`).then(unwrap<Recipe>),
  listCategories: () => fetch('/api/categories').then(unwrap<Category[]>),

  // favourites (per-user, keyed on the Cloudflare Access identity — #1)
  listFavourites: () => fetch('/api/favourites').then(unwrap<number[]>),
  addFavourite: (id: number) =>
    fetch(`/api/favourites/${id}`, { method: 'PUT' }).then(unwrap<{ ok: true }>),
  removeFavourite: (id: number) =>
    fetch(`/api/favourites/${id}`, { method: 'DELETE' }).then(unwrap<{ ok: true }>),

  // admin
  adminListRecipes: () =>
    fetch('/api/admin/recipes').then(
      unwrap<
        Array<{
          id: number;
          title: string;
          slug: string;
          photo_key: string | null;
          created_at: string;
          updated_at: string;
        }>
      >,
    ),
  adminGetRecipe: (id: number) => fetch(`/api/admin/recipes/${id}`).then(unwrap<Recipe>),
  adminCreateRecipe: (input: RecipeInput) =>
    fetch('/api/admin/recipes', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    }).then(unwrap<Recipe>),
  adminPatchRecipe: (id: number, patch: RecipePatch) =>
    fetch(`/api/admin/recipes/${id}`, {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify(patch),
    }).then(unwrap<Recipe>),
  adminDeleteRecipe: (id: number) =>
    fetch(`/api/admin/recipes/${id}`, { method: 'DELETE' }).then(unwrap<void>),

  adminCreateCategory: (input: CategoryInput) =>
    fetch('/api/admin/categories', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    }).then(unwrap<Category>),
  adminPatchCategory: (id: number, patch: CategoryPatch) =>
    fetch(`/api/admin/categories/${id}`, {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify(patch),
    }).then(unwrap<Category>),
  adminDeleteCategory: (id: number) =>
    fetch(`/api/admin/categories/${id}`, { method: 'DELETE' }).then(unwrap<void>),

  adminUploadPhoto: async (variants: PhotoVariant[]): Promise<{ key: string }> => {
    const form = new FormData();
    for (const v of variants) {
      form.append(`w${v.width}`, v.blob, `w${v.width}.webp`);
    }
    // No explicit Content-Type — the browser sets the multipart boundary.
    const res = await fetch('/api/admin/photos', { method: 'POST', body: form });
    return unwrap<{ key: string }>(res);
  },
};

function encodeKey(key: string): string {
  return key.split('/').map(encodeURIComponent).join('/');
}

// Legacy photos are stored as a single file with an image extension
// (recipes/<uuid>.jpg). New photos store a variant set under an extensionless
// base key (recipes/<uuid> → recipes/<uuid>-{400,800,1600}.webp).
function isLegacyKey(key: string): boolean {
  return /\.(jpe?g|png|webp)$/i.test(key);
}

export function photoUrl(key: string | null | undefined): string | null {
  if (!key) return null;
  if (isLegacyKey(key)) return `/api/photos/${encodeKey(key)}`;
  // Default <img src> fallback for variant sets is the mid-size 800px WebP.
  return `/api/photos/${encodeKey(`${key}-800.webp`)}`;
}

// srcset for the variant set, or undefined for legacy single-file photos
// (those just use the plain src above).
export function photoSrcSet(key: string | null | undefined): string | undefined {
  if (!key || isLegacyKey(key)) return undefined;
  return PHOTO_WIDTHS.map((w) => `/api/photos/${encodeKey(`${key}-${w}.webp`)} ${w}w`).join(', ');
}
